import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { EApplicantType } from '@if/core/types'
import { IpqsEmailDetail, IpqsPhoneDetail } from '@src/data/types/FraudAnalysisResult'
import { JSX, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  onClose: () => void
  emailDetail?: IpqsEmailDetail | null
  phoneDetail?: IpqsPhoneDetail | null
  applicantType: EApplicantType
  loading?: boolean
}


const FraudReportDialog = ({ open, onClose, emailDetail, phoneDetail, applicantType, loading }: Props) => {
  const { t } = useTranslation()
  const applicantTypeLabel = t(`common.${(applicantType === EApplicantType.Applicant ? 'applicant' : 'coApplicant')}`)
  const theme = useTheme()
  const [expandedPanel, setExpandedPanel] = useState<string | false>('email')

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false)
  }

  const formatBooleanValue = (value?: boolean): JSX.Element => {
    if (value === undefined) return <Typography variant="body2">{t('fraudReport.notAvailable')}</Typography>
    return (
      <Chip label={value ? t('fraudReport.yes') : t('fraudReport.no')} color={value ? 'error' : 'info'} size="small" />
    )
  }

  const formatScoreValue = (score?: number): JSX.Element => {
    if (score === undefined) return <Typography variant="body2">{t('fraudReport.notAvailable')}</Typography>

    let color: 'error' | 'warning' | 'success' = 'success'
    if (score >= 85) color = 'error'
    else if (score >= 75) color = 'warning'

    return <Chip label={score.toString()} color={color} size="small" />
  }

  const formatTextValue = (value?: string | number | boolean): JSX.Element => {
    if (value === undefined || value === null) {
      return <Typography variant="body2">{t('fraudReport.notAvailable')}</Typography>
    }

    return <Chip label={value.toString()} color={'info'} size="small" />
  }

  const renderTableRow = (label: string, value: JSX.Element | string) => (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', width: '40%' }}>
        {label}
      </TableCell>
      <TableCell>
        {typeof value === 'string' ? (
          <Chip
            label={value}
            size="small"
            sx={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
            }}
          />
        ) : (
          value
        )}
      </TableCell>
    </TableRow>
  )

  const renderEmailTable = (data: IpqsEmailDetail) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableBody>
          {renderTableRow(t('fraudReport.fraudScore'), formatScoreValue(data.fraudScore))}
          {renderTableRow(t('fraudReport.valid'), formatBooleanValue(data.valid))}
          {renderTableRow(t('fraudReport.disposable'), formatBooleanValue(data.disposable))}
          {renderTableRow(t('fraudReport.deliverability'), formatTextValue(data.deliverability))}
          {renderTableRow(t('fraudReport.recent'), formatBooleanValue(data.recentAbuse))}
          {renderTableRow(t('fraudReport.smtpScore'), formatTextValue(data.smtpScore))}
          {renderTableRow(t('fraudReport.overallScore'), formatTextValue(data.overallScore))}
          {renderTableRow(t('fraudReport.firstName'), formatTextValue(data.firstName))}
          {renderTableRow(t('fraudReport.catchAll'), formatBooleanValue(data.catchAll))}
          {renderTableRow(t('fraudReport.generic'), formatBooleanValue(data.generic))}
          {renderTableRow(t('fraudReport.common'), formatBooleanValue(data.common))}
          {renderTableRow(t('fraudReport.dnsValid'), formatBooleanValue(data.dnsValid))}
          {renderTableRow(t('fraudReport.honeypot'), formatBooleanValue(data.honeypot))}
          {renderTableRow(t('fraudReport.spam'), formatTextValue(data.spamTrapScore))}
          {renderTableRow(t('fraudReport.frequent'), formatBooleanValue(data.frequentComplainer))}
          {renderTableRow(t('fraudReport.suspect'), formatBooleanValue(data.suspect))}
          {renderTableRow(t('fraudReport.leaked'), formatBooleanValue(data.leaked))}
          {renderTableRow(t('fraudReport.sanitizedEmail'), formatTextValue(data.sanitizedEmail))}
          {data.domainAge && renderTableRow(t('fraudReport.domainAge'), formatTextValue(data.domainAge.human))}
          {renderTableRow(t('fraudReport.domain'), formatTextValue(data.domainTrust))}
          {renderTableRow(t('fraudReport.userActivity'), formatTextValue(data.userActivity))}
          {renderTableRow(t('fraudReport.domainVelocity'), formatTextValue(data.domainVelocity))}
          {data.associatedNames &&
            data.associatedNames.length > 0 &&
            renderTableRow(t('fraudReport.associatedNames'), data.associatedNames.join(', '))}
          {renderTableRow(t('fraudReport.suggestedDomain'), formatTextValue(data.suggestedDomain))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  const renderPhoneTable = (data: IpqsPhoneDetail) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableBody>
          {renderTableRow(t('fraudReport.fraudScore'), formatScoreValue(data.fraudScore))}
          {renderTableRow(t('fraudReport.phoneValidity'), formatBooleanValue(data.valid))}
          {renderTableRow(t('fraudReport.active'), formatBooleanValue(data.active))}
          {renderTableRow(t('fraudReport.activeStatus'), formatTextValue(data.activeStatus))}
          {renderTableRow(t('fraudReport.carrier'), formatTextValue(data.carrier))}
          {renderTableRow(t('fraudReport.lineType'), formatTextValue(data.lineType))}
          {renderTableRow(t('fraudReport.prepaid'), formatBooleanValue(data.prepaid))}
          {renderTableRow(t('fraudReport.voip'), formatBooleanValue(data.voip))}
          {renderTableRow(t('fraudReport.name'), formatTextValue(data.name))}
          {renderTableRow(t('fraudReport.country'), formatTextValue(data.country))}
          {renderTableRow(t('fraudReport.region'), formatTextValue(data.region))}
          {renderTableRow(t('fraudReport.city'), formatTextValue(data.city))}
          {renderTableRow(t('fraudReport.zipCode'), formatTextValue(data.zipCode))}
          {renderTableRow(t('fraudReport.timezone'), formatTextValue(data.timezone))}
          {renderTableRow(t('fraudReport.leaked'), formatBooleanValue(data.leaked))}
          {renderTableRow(t('fraudReport.formattedPhoneNumber'), formatTextValue(data.formatted))}
          {renderTableRow(t('fraudReport.localFormat'), formatTextValue(data.localFormat))}
          {renderTableRow(t('fraudReport.smsEmail'), formatTextValue(data.smsDomain))}
          {renderTableRow(t('fraudReport.mnc'), formatTextValue(data.mnc))}
          {renderTableRow(t('fraudReport.mcc'), formatTextValue(data.mcc))}
          {data.associatedNames &&
            data.associatedNames.length > 0 &&
            renderTableRow(t('fraudReport.associatedNames'), data.associatedNames.join(', '))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  const renderReportContent = (type: 'email' | 'phone') => {
    const data = type === 'email' ? emailDetail : phoneDetail

    if (!data) {
      return (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('fraudReport.noData')}</Typography>
        </Paper>
      )
    }

    if (data.success === false) {
      return (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Stack spacing={1}>
            <Typography color="error" variant="subtitle1">
              {t('fraudReport.message')}: {data.message}
            </Typography>
            {data.requestId && (
              <Typography variant="body2" color="text.secondary">
                {t('fraudReport.requestId')}: {data.requestId}
              </Typography>
            )}
          </Stack>
        </Paper>
      )
    }

    return type === 'email' ? renderEmailTable(data as IpqsEmailDetail) : renderPhoneTable(data as IpqsPhoneDetail)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div">
          {t('fraudReport.title')} ({applicantTypeLabel})
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {loading ? (
          <Typography>{t('common.loading')}</Typography>
        ) : (
          <Box>
            <Accordion expanded={expandedPanel === 'email'} onChange={handleAccordionChange('email')} sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  '&.Mui-expanded': {
                    minHeight: 48,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  {t('fraudReport.emailAnalysis')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>{renderReportContent('email')}</AccordionDetails>
            </Accordion>

            <Accordion expanded={expandedPanel === 'phone'} onChange={handleAccordionChange('phone')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  '&.Mui-expanded': {
                    minHeight: 48,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  {t('fraudReport.phoneAnalysis')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>{renderReportContent('phone')}</AccordionDetails>
            </Accordion>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} variant="contained">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FraudReportDialog
